import { Link } from "react-router-dom";

// Deterministic palette: cream / lime / ink mood blends. Keeps everything
// on-brand even when 100 strangers show up in attendee lists.
const PALETTE = [
  { bg: "#D7FF2F", fg: "#0A0A0A" }, // lime / ink
  { bg: "#0A0A0A", fg: "#F4F0E1" }, // ink / cream
  { bg: "#F4F0E1", fg: "#0A0A0A" }, // cream / ink
  { bg: "#C8736B", fg: "#F4F0E1" }, // brick / cream
  { bg: "#5A7FB2", fg: "#F4F0E1" }, // blue / cream
  { bg: "#7BA46C", fg: "#0A0A0A" }, // sage / ink
  { bg: "#D17A47", fg: "#0A0A0A" }, // ochre / ink
  { bg: "#9C6BC8", fg: "#F4F0E1" }, // violet / cream
];

// Cheap, stable hash → palette index.
function hashIndex(seed) {
  if (!seed) return 0;
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % PALETTE.length;
}

const SIZES = {
  xs:  { box: "w-7  h-7  text-[10px]" },
  sm:  { box: "w-9  h-9  text-xs"  },
  md:  { box: "w-12 h-12 text-base" },
  lg:  { box: "w-16 h-16 text-xl"  },
  xl:  { box: "w-24 h-24 text-2xl" },
  "2xl": { box: "w-36 h-36 md:w-44 md:h-44 text-4xl md:text-5xl" },
};

function initialsFrom(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase() || "·";
}

/**
 * Avatar — circular, image w/ deterministic initials fallback.
 *   - When `src` is missing, renders a chunky initials tile coloured from a
 *     stable hash of (name || seed). Same user → same colour, every time.
 *   - Always circular now (rounded-full) for consistency across navbar,
 *     profile, attendee lists, comments, admin panel and event cards.
 */
export default function Avatar({
  src,
  name,
  seed,
  size = "md",
  glow = false,
  to = null,
  testid,
  className = "",
}) {
  const cls = SIZES[size]?.box || SIZES.md.box;
  const ring = glow
    ? "ring-2 ring-neon ring-offset-2 ring-offset-ink shadow-[0_0_28px_rgba(214,255,0,0.35)]"
    : "";

  const palette = PALETTE[hashIndex(seed || name || "anon")];

  const inner = src ? (
    <img
      src={src}
      alt={name || "avatar"}
      loading="lazy"
      className="w-full h-full object-cover"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      data-testid={testid ? `${testid}-img` : undefined}
    />
  ) : (
    <span
      data-testid={testid ? `${testid}-initials` : undefined}
      className="font-display font-bold select-none w-full h-full flex items-center justify-center"
      style={{ background: palette.bg, color: palette.fg }}
    >
      {initialsFrom(name)}
    </span>
  );

  const base = `relative ${cls} ${ring} ${className} shrink-0 border border-ink/15 bg-paper rounded-full flex items-center justify-center overflow-hidden`;

  if (to) {
    return (
      <Link to={to} data-testid={testid} className={base + " hover:border-neon transition-colors"}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className={base}>
      {inner}
    </div>
  );
}
